import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { updateCompanyName, updateCompanyInfo } from "@/lib/stores/companyStore";
import SaveBar from "./SaveBar";
import { Input } from  "@/components/ui/InputDefault";
import { getScriptOptions } from "@/lib/dataSources/scriptTypes";
import { useRTL } from "@/providers/RTLProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export default function GeneralSetupSection() {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame, selectedCompany, selectCompany } =
    useSimplifiedUserData();
  const { currentScript, scriptMessage } = useRTL();

  // Current values
  const [companyName, setCompanyName] = useState("");
  const [settingTitle, setSettingTitle] = useState("Default");
  const [displayInNav, setDisplayInNav] = useState(true);
  const [twoFactorOption, setTwoFactorOption] = useState("dont-require");
  const [scriptType, setScriptType] = useState("latin");

  const availableScripts = getScriptOptions();

  // Original values for change tracking
  const [originalValues, setOriginalValues] = useState({
    companyName: "",
    settingTitle: "Default",
    displayInNav: true,
    twoFactorOption: "dont-require",
    scriptType: "latin",
  });

  // Update company settings when selected company changes
  useEffect(() => {
    if (selectedCompany) {
      setCompanyName(selectedCompany.name || "");
      const companyScriptType = selectedCompany.settings?.scriptType || "latin";
      setScriptType(companyScriptType);
      setOriginalValues((prev) => ({
        ...prev,
        companyName: selectedCompany.name || "",
        scriptType: companyScriptType,
      }));
    }
  }, [selectedCompany]);

  // Check if there are any changes
  const hasChanges =
    companyName !== originalValues.companyName ||
    settingTitle !== originalValues.settingTitle ||
    displayInNav !== originalValues.displayInNav ||
    twoFactorOption !== originalValues.twoFactorOption ||
    scriptType !== originalValues.scriptType;

  // Save changes function
  const handleSave = async () => {
    if (!selectedCompany) {
      toast({
        title: "Error",
        description: "No company selected",
        variant: "destructive",
      });
      return;
    }

    try {
      // Prepare updates
      const updates: any = {};
      let needsUpdate = false;

      if (companyName !== originalValues.companyName) {
        updates.name = companyName;
        needsUpdate = true;
      }

      if (scriptType !== originalValues.scriptType) {
        updates.settings = {
          ...selectedCompany.settings,
          scriptType: scriptType
        };
        needsUpdate = true;
      }

      // Update company if there are changes
      if (needsUpdate) {
        const updatedCompany = await updateCompanyInfo(
          selectedCompany.id,
          updates,
          toast
        );

        // Update the global selected company context
        selectCompany(updatedCompany.id);

        toast({
          title: "Success",
          description: "Company settings updated successfully",
        });
      }

      // TODO: Save other settings (settingTitle, displayInNav, twoFactorOption)
      // These would typically be saved to user preferences or company settings

      // Update original values to match current values
      setOriginalValues({
        companyName,
        settingTitle,
        displayInNav,
        twoFactorOption,
        scriptType,
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    }
  };

  // Cancel changes function
  const handleCancel = () => {
    setCompanyName(originalValues.companyName);
    setSettingTitle(originalValues.settingTitle);
    setDisplayInNav(originalValues.displayInNav);
    setTwoFactorOption(originalValues.twoFactorOption);
    setScriptType(originalValues.scriptType);
  };

  return (
    <div>
      <div className="max-w-2xl space-y-6  ">
        {/* Company Name */}
        <div>
          <label
            htmlFor="company-name"
            className="block text-sm font-medium text-foreground mb-2"
          >
            Company Name
          </label>
          <Input
            type="text"
            id="company-name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t('platform.settings.generalsetupsection.enter_the_company_name')}
            className="w-full"
          />
        </div>

        {/* Script Type / Writing System */}
        <div>
          <label
            htmlFor="company-script-type"
            className="block text-sm font-medium text-foreground mb-2"
          >
            Default Script / Writing System
          </label>
          <select
            id="company-script-type"
            value={scriptType}
            onChange={(e) => setScriptType(e.target.value)}
            className="w-full px-3 py-2 border bg-background border-input rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {availableScripts.map(script => (
              <option key={script.value} value={script.value}>
                {script.label} ({script.direction.toUpperCase()})
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            This will be the default script type for all members in this company. Individual users can override this in their profile settings.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Current: {scriptMessage}
          </p>
        </div>

        {/* Two-factor Authentication */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4">
            Two-factor authentication (2FA)
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            We'll prompt your project members to enable 2FA on their next login.
            To see who has enabled 2FA, check the{" "}
            <a href="#" className="text-primary hover:text-primary">
              User Management page
            </a>
            .
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            <a href="#" className="text-primary hover:text-primary">
              Learn more
            </a>{" "}
            about 2FA.
          </p>

          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="dont-require-2fa"
                type="radio"
                name="two-factor"
                value="dont-require"
                checked={twoFactorOption === "dont-require"}
                onChange={(e) => setTwoFactorOption(e.target.value)}
                className="h-4 w-4 text-primary focus:ring-primary border-input"
              />
              <label
                htmlFor="dont-require-2fa"
                className="ml-2 text-sm text-foreground"
              >
                Don't require 2FA
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="require-2fa"
                type="radio"
                name="two-factor"
                value="require"
                checked={twoFactorOption === "require"}
                onChange={(e) => setTwoFactorOption(e.target.value)}
                className="h-4 w-4 text-primary focus:ring-primary border-input"
              />
              <label
                htmlFor="require-2fa"
                className="ml-2 text-sm text-foreground"
              >
                Require 2FA (members are required to set up 2FA by their third
                login)
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="immediately-require-2fa"
                type="radio"
                name="two-factor"
                value="immediately-require"
                checked={twoFactorOption === "immediately-require"}
                onChange={(e) => setTwoFactorOption(e.target.value)}
                className="h-4 w-4 text-primary focus:ring-primary border-input"
              />
              <label
                htmlFor="immediately-require-2fa"
                className="ml-2 text-sm text-foreground"
              >
                Immediately require 2FA (members will be logged out and required
                to set up 2FA)
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Save Bar */}
      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />
    </div>
  );
}
